import React, { useState } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';

const HRReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [timeRange, setTimeRange] = useState('last30Days');
  
  const reportData = {
    departmentData: [
      { name: 'Engineering', value: 35, color: '#5AB9EA' },
      { name: 'Sales', value: 25, color: '#84CEEB' },
      { name: 'Marketing', value: 20, color: '#5680E9' },
      { name: 'Operations', value: 15, color: '#F8A055' },
      { name: 'HR', value: 10, color: '#C1C8E4' },
      { name: 'Finance', value: 5, color: '#8860D0' }
    ],
    employeeData: [
      { month: 'Jan', headcount: 120, turnover: 2, engagement: 85 },
      { month: 'Feb', headcount: 125, turnover: 1, engagement: 86 },
      { month: 'Mar', headcount: 130, turnover: 3, engagement: 87 },
      { month: 'Apr', headcount: 135, turnover: 2, engagement: 88 },
      { month: 'May', headcount: 140, turnover: 1, engagement: 89 },
      { month: 'Jun', headcount: 145, turnover: 2, engagement: 90 }
    ],
    trainingData: [
      { program: 'Leadership', completion: 85, satisfaction: 92 },
      { program: 'Technical', completion: 90, satisfaction: 88 },
      { program: 'Soft Skills', completion: 88, satisfaction: 90 },
      { program: 'Compliance', completion: 95, satisfaction: 85 },
      { program: 'Onboarding', completion: 100, satisfaction: 94 },
      { program: 'Career Dev', completion: 82, satisfaction: 89 }
    ],
    kpiData: [
      { metric: 'Employee Retention', target: 95, actual: 96, variance: 1 },
      { metric: 'Time to Hire', target: 30, actual: 28, variance: -2 },
      { metric: 'Training Completion', target: 90, actual: 88, variance: -2 },
    ],
    metrics: {
      employeeCount: 145,
      avgTenure: '3.5 years',
      turnoverRate: 8.5,
      engagementScore: 90,
      diversityScore: 85,
      trainingHours: 1200,
      recruitmentCost: 45000,
      benefitsCost: 850000
    },
    topInitiatives: [
      { title: 'Employee Wellness Program', impact: '15% engagement increase' },
      { title: 'Leadership Development', impact: '20% promotion rate' },
      { title: 'Diversity & Inclusion', impact: '25% more diverse hires' }
    ]
  };

  // Calculate metrics
  const totalTurnover = reportData.employeeData.reduce((sum, item) => sum + item.turnover, 0);
  const avgEngagement = reportData.employeeData.reduce((sum, item) => sum + item.engagement, 0) / reportData.employeeData.length;
  const avgTrainingCompletion = reportData.trainingData.reduce((sum, item) => sum + item.completion, 0) / reportData.trainingData.length;
  const avgTrainingSatisfaction = reportData.trainingData.reduce((sum, item) => sum + item.satisfaction, 0) / reportData.trainingData.length;

  const handleExport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.text('HR Analytics Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Overview Section
    doc.setFontSize(16);
    doc.text('Overview', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    doc.text(`Employee Count: ${reportData.metrics.employeeCount}`, 20, yPos);
    yPos += 7;
    doc.text(`Engagement Score: ${reportData.metrics.engagementScore}%`, 20, yPos);
    yPos += 7;
    doc.text(`Turnover Rate: ${reportData.metrics.turnoverRate}%`, 20, yPos);
    yPos += 15;

    // Training & Development
    doc.setFontSize(16);
    doc.text('Training & Development', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    doc.text(`Training Hours: ${reportData.metrics.trainingHours}`, 20, yPos);
    yPos += 7;
    doc.text(`Average Completion: ${avgTrainingCompletion.toFixed(1)}%`, 20, yPos);
    yPos += 15;

    // Department Distribution
    doc.setFontSize(16);
    doc.text('Department Distribution', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    reportData.departmentData.forEach(dept => {
      doc.text(`${dept.name}: ${dept.value}%`, 20, yPos);
      yPos += 7;
    });
    yPos += 8;

    // KPI Performance
    doc.setFontSize(16);
    doc.text('KPI Performance', 20, yPos);
    yPos += 10;
    doc.setFontSize(12);
    reportData.kpiData.forEach(kpi => {
      doc.text(`${kpi.metric}: ${kpi.actual}% (Target: ${kpi.target}%, Variance: ${kpi.variance}%)`, 20, yPos);
      yPos += 7;
    });

    // Save the PDF
    doc.save('hr-analytics-report.pdf');
  };

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <BackButton onClick={() => navigate('/crm/reports')} />
          <h1 className={`text-3xl font-bold tracking-tight flex-1 text-center text-gray-900 dark:text-white`}>HR Analytics</h1>
          <div className="w-[100px]"></div> {/* Spacer to balance the back button */}
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <select 
              className={`px-4 py-2 border rounded-md shadow-sm ${
                darkMode 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300'
              }`}
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="last7Days">Last 7 Days</option>
              <option value="last30Days">Last 30 Days</option>
              <option value="last90Days">Last 90 Days</option>
              <option value="ytd">Year to Date</option>
            </select>
            <button 
              onClick={handleExport}
              className="px-4 py-2 bg-red-600 hover:bg-red-800 text-white rounded-md transition-colors duration-200"
            >
              Export
            </button>
          </div>
        </div>

        {/* Overview Section */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
          <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-blue-900' : 'bg-blue-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-gray-500'}`}>Positions Filled</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-blue-100' : 'text-gray-900'}`}>45</span>
            </div>
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-green-900' : 'bg-green-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-green-300' : 'text-gray-500'}`}>Placement Success Rate</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-green-100' : 'text-gray-900'}`}>92%</span>
            </div>
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-purple-900' : 'bg-purple-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-purple-300' : 'text-gray-500'}`}>Average Time to Fill</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-purple-100' : 'text-gray-900'}`}>28 days</span>
            </div>
          </div>
        </div>

        {/* Position Distribution & Request Distribution */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Position Request Distribution</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={reportData.departmentData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label
                  >
                    {reportData.departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ paddingLeft: '20px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Position Filled Distribution</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={reportData.departmentData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label
                  >
                    {reportData.departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ paddingLeft: '20px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Candidate Funnel Stats */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
          <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Candidate Funnel Stats</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-blue-900' : 'bg-blue-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-gray-500'}`}>Applications Received</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-blue-100' : 'text-gray-900'}`}>156</span>
            </div>
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-green-900' : 'bg-green-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-green-300' : 'text-gray-500'}`}>Screened Candidates</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-green-100' : 'text-gray-900'}`}>98</span>
            </div>
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-yellow-900' : 'bg-yellow-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-gray-500'}`}>Interviews Scheduled</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-yellow-100' : 'text-gray-900'}`}>45</span>
            </div>
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-orange-900' : 'bg-orange-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-orange-300' : 'text-gray-500'}`}>Offers Made</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-orange-100' : 'text-gray-900'}`}>32</span>
            </div>
            <div className={`flex flex-col p-4 rounded-lg text-center ${
              darkMode ? 'bg-purple-900' : 'bg-purple-50'
            }`}>
              <span className={`text-sm ${darkMode ? 'text-purple-300' : 'text-gray-500'}`}>Offers Accepted</span>
              <span className={`text-2xl font-bold ${darkMode ? 'text-purple-100' : 'text-gray-900'}`}>28</span>
            </div>
          </div>
        </div>

        {/* Cost Analysis & Initiatives */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-8 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Hour Expenditure</h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { month: 'Jan', hours: 420 },
                  { month: 'Feb', hours: 380 },
                  { month: 'Mar', hours: 450 },
                  { month: 'Apr', hours: 410 },
                  { month: 'May', hours: 480 },
                  { month: 'Jun', hours: 440 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="hours" name="Hour Expenditure" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className={`text-xl font-semibold mb-8 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Retention Metrics</h2>
            <div className="flex flex-col gap-6 h-64">
              <div className="grid grid-cols-3 gap-6 h-1/2">
                <div className={`flex flex-col p-8 rounded-lg text-center justify-center ${
                  darkMode ? 'bg-blue-900' : 'bg-blue-50'
                }`}>
                  <span className={`text-sm mb-3 ${darkMode ? 'text-blue-300' : 'text-gray-500'}`}>30-Day Retention</span>
                  <span className={`text-4xl font-bold ${darkMode ? 'text-blue-100' : 'text-gray-900'}`}>98%</span>
                </div>
                <div className={`flex flex-col p-8 rounded-lg text-center justify-center ${
                  darkMode ? 'bg-green-900' : 'bg-green-50'
                }`}>
                  <span className={`text-sm mb-3 ${darkMode ? 'text-green-300' : 'text-gray-500'}`}>60-Day Retention</span>
                  <span className={`text-4xl font-bold ${darkMode ? 'text-green-100' : 'text-gray-900'}`}>95%</span>
                </div>
                <div className={`flex flex-col p-8 rounded-lg text-center justify-center ${
                  darkMode ? 'bg-yellow-900' : 'bg-yellow-50'
                }`}>
                  <span className={`text-sm mb-3 ${darkMode ? 'text-yellow-300' : 'text-gray-500'}`}>90-Day Retention</span>
                  <span className={`text-4xl font-bold ${darkMode ? 'text-yellow-100' : 'text-gray-900'}`}>92%</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6 h-1/2">
                <div className={`flex flex-col p-8 rounded-lg text-center justify-center ${
                  darkMode ? 'bg-orange-900' : 'bg-orange-50'
                }`}>
                  <span className={`text-sm mb-3 ${darkMode ? 'text-orange-300' : 'text-gray-500'}`}>6-Month Retention</span>
                  <span className={`text-4xl font-bold ${darkMode ? 'text-orange-100' : 'text-gray-900'}`}>88%</span>
                </div>
                <div className={`flex flex-col p-8 rounded-lg text-center justify-center ${
                  darkMode ? 'bg-purple-900' : 'bg-purple-50'
                }`}>
                  <span className={`text-sm mb-3 ${darkMode ? 'text-purple-300' : 'text-gray-500'}`}>1-Year Retention</span>
                  <span className={`text-4xl font-bold ${darkMode ? 'text-purple-100' : 'text-gray-900'}`}>85%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* AI Insights */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>AI-Powered HR Insights</h2>
          <div className={`p-4 rounded-xl mb-4 bg-blue-50 dark:bg-blue-900/40 border border-blue-100 dark:border-blue-800/50`}>
            <h3 className={`font-medium mb-2 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Optimization Recommendations</h3>
            <ul className="list-disc pl-5">
              <li className={`mb-1 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Implement flexible work arrangements to improve retention by 15%</li>
              <li className={`mb-1 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Enhance career development programs based on employee feedback</li>
              <li className={`mb-1 ${darkMode ? 'text-blue-200' : 'text-gray-900'}`}>Optimize recruitment process to reduce time-to-hire by 20%</li>
            </ul>
          </div>
          <div className={`p-4 rounded-xl bg-purple-50 dark:bg-purple-900/40 border border-purple-100 dark:border-purple-800/50`}>
            <h3 className={`font-medium mb-2 ${darkMode ? 'text-purple-200' : 'text-gray-900'}`}>Performance Predictions</h3>
            <p className={darkMode ? 'text-purple-200' : 'text-gray-900'}>Based on current trends, we predict a 10% increase in employee engagement next quarter if recommended initiatives are implemented.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRReports; 